import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';

import { CAN_APPROVE_GOVERNANCE } from './live/operations';

/**
 * Reported once per page load, not once per failure.
 *
 * The failure this exists to catch is systemic — the api running the feature
 * dark while the webapp's allowlist is not, so the field is absent from the
 * schema and the query fails validation for EVERY viewer on EVERY navigation.
 * Capturing each occurrence would put one event per page view per user into
 * Sentry for as long as the skew lasts, which is a permanently lit alarm nobody
 * can read rather than a signal. One event per session is enough to see it.
 */
let reportedThisSession = false;

/**
 * Whether the viewer may take governance decisions, for the Approvals nav gate
 * (ENG-5654).
 *
 * The item used to render for every non-expert while the queue behind it was
 * approver-only, so everyone on a team except its owner carried a permanently
 * empty entry — internal staff included, since `canViewEngagementSurfaces`
 * short-circuits on `viewer.admin`. This is what lets the nav follow the queue.
 *
 * `enabled` MUST be passed, and should fold in every condition the caller
 * already knows would suppress the item — the feature gate, and customer-side,
 * since an expert never sees Approvals whatever the answer would be. The api
 * field lives behind `ENABLE_CONSULTING_ENGAGEMENTS`, so it is absent from the
 * schema in an environment running the feature dark. Skipping keeps the request
 * off the wire for every viewer the answer could not change anything for.
 *
 * Answers ELIGIBILITY, not queue contents: an approver who has just cleared
 * their queue is still true, so the item does not disappear when they catch up.
 *
 * FALSE IS ALSO THE FAILURE VALUE. Loading returns false, and so does an error:
 * the nav renders no item rather than flashing one that a moment later proves
 * wrong, and failing closed is the right direction for a route into irreversible
 * financial decisions. The cost is that a failure is indistinguishable from a
 * genuine "not an approver" — the Approvals item simply is not there, with
 * nothing in the UI to say the question was never answered. That is why the
 * error is reported rather than merely absorbed: without it, an environment
 * whose flags have drifted apart looks exactly like one where nobody governs.
 */
export function useIsGovernanceApprover(enabled: boolean): boolean {
  const { data, error } = useQuery(CAN_APPROVE_GOVERNANCE, {
    skip: !enabled,
    // The answer changes only when team ownership does, which cannot happen
    // from inside this session — so serve it from cache on later navigations
    // rather than re-asking on every mount.
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (!error || reportedThisSession) return;
    reportedThisSession = true;
    // `level: 'warning'`, matching MakeOfferDrawer: the viewer is not broken and
    // nothing was lost, but a surface they may be entitled to is missing and
    // only this line says so.
    Sentry.captureException(error, {
      level: 'warning',
      tags: { engagement_approver_gate_failed: 'true' },
    });
  }, [error]);

  return data?.canApproveGovernance ?? false;
}
